import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queueLessonAddedEmails } from '@/lib/lms/queueLmsEmail';

export interface AdminCourse {
  id: string;
  brand_id: string;
  title: string;
  description: string;
  thumbnail_url?: string;
  inner_page_image?: string | null;
  instructor_id?: string[] | null;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  estimated_duration: number;
  is_published: boolean;
  is_free?: boolean;
  price?: number;
  sale_price?: number;
  stripe_product_id?: string;
  stripe_price_id?: string;
  tag_ids?: string[];
  // Physical course fields
  start_date?: string | null;
  location?: string | null;
  dates?: string | null;
  duration_text?: string | null;
  landing_page_url?: string | null;
  course_type_id?: string | null;
  course_type?: { is_digital_material: boolean } | null;
  audio_languages?: any;
  subtitles_languages?: any;
  testimonial_ids?: string[];
  client_logo_gallery_ids?: string[];
  client_logo_ids?: string[];
  // Product integration
  product_id?: string | null;
  course_evaluation_form_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminModule {
  id: string;
  brand_id: string;
  course_id: string;
  title: string;
  description: string;
  sort_order: number;
  is_published: boolean;
  tag_ids?: string[];
}

export interface AdminLesson {
  id: string;
  brand_id: string;
  module_id: string;
  title: string;
  description?: string | null;
  content_type: 'video' | 'text' | 'pdf' | 'quiz' | 'mixed';
  content: any;
  duration: number;
  sort_order: number;
  is_preview: boolean;
  is_published: boolean;
  material_url?: string | null;
  tag_ids?: string[];
  transcript?: string | null;
  lesson_evaluation_form_id?: string | null;
}

export function useAdminCourses(brandId: string | null) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: courses, isLoading } = useQuery({
    queryKey: ['admin-courses', brandId],
    queryFn: async () => {
      if (!brandId) return [];

      const { data, error } = await supabase
        .from('os_lms_courses')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as AdminCourse[];
    },
    enabled: !!brandId,
  });

  const createCourse = useMutation({
    mutationFn: async (course: Omit<AdminCourse, 'id' | 'created_at' | 'updated_at'>) => {
      // Generate base slug from title
      const baseSlug = course.title
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      
      // Check for existing slugs and find an available one
      let slug = baseSlug;
      let suffix = 1;
      let slugExists = true;
      
      while (slugExists) {
        const { data: existingCourse, error: checkError } = await supabase
          .from('os_lms_courses')
          .select('id')
          .eq('brand_id', course.brand_id)
          .eq('slug', slug)
          .maybeSingle();
        
        if (checkError) throw checkError;
        
        if (existingCourse) {
          suffix++;
          slug = `${baseSlug}-${suffix}`;
        } else {
          slugExists = false;
        }
      }
      
      const { data, error } = await supabase
        .from('os_lms_courses')
        .insert([{ ...course, slug, category: null }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', brandId] });
      toast({ title: 'Course created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create course', description: error.message, variant: 'destructive' });
    },
  });

  const updateCourse = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AdminCourse> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_lms_courses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', brandId] });
      toast({ title: 'Course updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update course', description: error.message, variant: 'destructive' });
    },
  });

  const deleteCourse = useMutation({
    mutationFn: async (courseId: string) => {
      const { error } = await supabase
        .from('os_lms_courses')
        .delete()
        .eq('id', courseId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', brandId] });
      toast({ title: 'Course deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to delete course', description: error.message, variant: 'destructive' });
    },
  });

  const cloneCourse = useMutation({
    mutationFn: async (courseId: string) => {
      // Fetch the original course
      const { data: originalCourse, error: fetchError } = await supabase
        .from('os_lms_courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (fetchError) throw fetchError;
      if (!originalCourse) throw new Error('Course not found');

      // Prepare clone data - exclude id, timestamps and product_id
      const { id, created_at, updated_at, product_id, slug: originalSlug, ...courseData } = originalCourse;
      
      // Generate new title with "copy: " prefix
      const newTitle = `copy: ${courseData.title}`;
      
      // Generate unique slug
      const baseSlug = newTitle
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      
      let slug = baseSlug;
      let suffix = 1;
      let slugExists = true;
      
      while (slugExists) {
        const { data: existingCourse } = await supabase
          .from('os_lms_courses')
          .select('id')
          .eq('brand_id', courseData.brand_id)
          .eq('slug', slug)
          .maybeSingle();
        
        if (existingCourse) {
          suffix++;
          slug = `${baseSlug}-${suffix}`;
        } else {
          slugExists = false;
        }
      }

      // Insert cloned course
      const { data: clonedCourse, error: insertError } = await supabase
        .from('os_lms_courses')
        .insert([{ 
          ...courseData, 
          title: newTitle,
          slug,
          is_published: false // Clone as draft
        }])
        .select()
        .single();

      if (insertError) throw insertError;
      return clonedCourse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-courses', brandId] });
      toast({ title: 'Course cloned successfully' });
      return data;
    },
    onError: (error: any) => {
      toast({ title: 'Failed to clone course', description: error.message, variant: 'destructive' });
    },
  });

  return {
    courses,
    isLoading,
    createCourse: createCourse.mutate,
    updateCourse: updateCourse.mutate,
    deleteCourse: deleteCourse.mutate,
    cloneCourse: cloneCourse.mutateAsync,
    isCreating: createCourse.isPending,
    isUpdating: updateCourse.isPending,
    isDeleting: deleteCourse.isPending,
    isCloning: cloneCourse.isPending,
  };
}

export function useAdminCourseDetails(courseId: string | null, brandId?: string | null) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: course, isLoading: isCourseLoading } = useQuery({
    queryKey: ['admin-course', courseId, brandId],
    queryFn: async () => {
      if (!courseId) return null;

      let query = supabase
        .from('os_lms_courses')
        .select('*, course_type:os_lms_course_types!course_type_id(is_digital_material)')
        .eq('id', courseId);
      
      // Add brand filter if brandId is provided (security check)
      if (brandId) {
        query = query.eq('brand_id', brandId);
      }
      
      const { data, error } = await query.single();

      if (error) throw error;
      
      // Handle instructor_id as array or single value
      let instructorIds: string[] = [];
      if ((data as any).instructor_id) {
        if (Array.isArray((data as any).instructor_id)) {
          instructorIds = (data as any).instructor_id;
        } else {
          // Single value - convert to array for backward compatibility
          instructorIds = [(data as any).instructor_id];
        }
      }

      return {
        ...data,
        instructor_ids: instructorIds,
      } as AdminCourse & { instructor_ids?: string[] };
    },
    enabled: !!courseId,
  });

  const { data: modules, isLoading: isModulesLoading } = useQuery({
    queryKey: ['admin-modules', courseId],
    queryFn: async () => {
      if (!courseId) return [];

      const { data, error } = await supabase
        .from('os_lms_modules')
        .select('*')
        .eq('course_id', courseId)
        .order('sort_order');

      if (error) throw error;
      return data as AdminModule[];
    },
    enabled: !!courseId,
  });

  const createModule = useMutation({
    mutationFn: async (module: Omit<AdminModule, 'id'>) => {
      const { data, error } = await supabase
        .from('os_lms_modules')
        .insert(module)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules', courseId] });
      toast({ title: 'Module created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create module', description: error.message, variant: 'destructive' });
    },
  });

  const updateModule = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AdminModule> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_lms_modules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules', courseId] });
      toast({ title: 'Module updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update module', description: error.message, variant: 'destructive' });
    },
  });

  const deleteModule = useMutation({
    mutationFn: async (moduleId: string) => {
      const { error } = await supabase
        .from('os_lms_modules')
        .delete()
        .eq('id', moduleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-modules', courseId] });
      toast({ title: 'Module deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to delete module', description: error.message, variant: 'destructive' });
    },
  });

  return {
    course,
    modules,
    isLoading: isCourseLoading || isModulesLoading,
    createModule: createModule.mutate,
    updateModule: updateModule.mutate,
    deleteModule: deleteModule.mutate,
  };
}

export function useAdminLessons(moduleId: string | null) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: lessons, isLoading } = useQuery({
    queryKey: ['admin-lessons', moduleId],
    queryFn: async () => {
      if (!moduleId) return [];

      const { data, error } = await supabase
        .from('os_lms_lessons')
        .select('*')
        .eq('module_id', moduleId)
        .order('sort_order');

      if (error) throw error;
      
      // Handle instructor_id as array or single value
      return (data || []).map((lesson: any) => {
        let instructorIds: string[] = [];
        if (lesson.instructor_id) {
          if (Array.isArray(lesson.instructor_id)) {
            instructorIds = lesson.instructor_id;
          } else {
            // Single value - convert to array for backward compatibility
            instructorIds = [lesson.instructor_id];
          }
        }
        return {
          ...lesson,
          instructor_ids: instructorIds,
        } as AdminLesson & { instructor_ids?: string[] };
      });
    },
    enabled: !!moduleId,
  });

  const createLesson = useMutation({
    mutationFn: async (lesson: Omit<AdminLesson, 'id'>) => {
      const { data, error } = await supabase
        .from('os_lms_lessons')
        .insert(lesson)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-lessons', moduleId] });
      toast({ title: 'Lesson created successfully' });

      // Queue lesson added emails only if created with is_published = true
      if (data && moduleId && data.is_published === true) {
        await triggerLessonAddedEmails(data.title);
      }
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create lesson', description: error.message, variant: 'destructive' });
    },
  });

  const updateLesson = useMutation({
    mutationFn: async ({ id, wasPublished, ...updates }: Partial<AdminLesson> & { id: string; wasPublished?: boolean }) => {
      const { data, error } = await supabase
        .from('os_lms_lessons')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, _wasPublished: wasPublished };
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-lessons', moduleId] });
      toast({ title: 'Lesson updated successfully' });

      // Queue lesson added emails when is_published changes to true
      if (data && moduleId && data.is_published === true && data._wasPublished !== true) {
        await triggerLessonAddedEmails(data.title);
      }
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update lesson', description: error.message, variant: 'destructive' });
    },
  });

  // Helper to queue lesson added emails with toast feedback
  const triggerLessonAddedEmails = async (lessonTitle: string) => {
    if (!moduleId) return;
    try {
      const { data: moduleData } = await supabase
        .from('os_lms_modules')
        .select('course_id, os_lms_courses(title, brand_id)')
        .eq('id', moduleId)
        .single();

      if (moduleData?.course_id && (moduleData as any).os_lms_courses) {
        const course = (moduleData as any).os_lms_courses;
        const result = await queueLessonAddedEmails({
          brandId: course.brand_id,
          courseId: moduleData.course_id,
          courseTitle: course.title,
          lessonName: lessonTitle,
        });
        
        // Show success/failure feedback
        if (result.queued > 0) {
          toast({
            title: `${result.queued} email(s) queued`,
            description: `Notification emails queued for "${lessonTitle}"`,
          });
        } else if (result.errors.length > 0) {
          toast({
            title: 'Email queuing failed',
            description: result.errors[0],
            variant: 'destructive',
          });
        }
      }
    } catch (emailError: any) {
      console.error('Error queueing lesson added emails:', emailError);
      toast({
        title: 'Email queuing error',
        description: emailError?.message || 'Failed to queue notification emails',
        variant: 'destructive',
      });
    }
  };

  const deleteLesson = useMutation({
    mutationFn: async (lessonId: string) => {
      const { error } = await supabase
        .from('os_lms_lessons')
        .delete()
        .eq('id', lessonId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lessons', moduleId] });
      toast({ title: 'Lesson deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to delete lesson', description: error.message, variant: 'destructive' });
    },
  });

  // Move lesson to another module
  const moveLesson = useMutation({
    mutationFn: async ({ lessonId, targetModuleId }: { lessonId: string; targetModuleId: string }) => {
      // Get the max sort_order in the target module
      const { data: targetLessons } = await supabase
        .from('os_lms_lessons')
        .select('sort_order')
        .eq('module_id', targetModuleId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const newSortOrder = targetLessons && targetLessons.length > 0 
        ? targetLessons[0].sort_order + 1 
        : 0;

      const { data, error } = await supabase
        .from('os_lms_lessons')
        .update({ module_id: targetModuleId, sort_order: newSortOrder })
        .eq('id', lessonId)
        .select()
        .single();

      if (error) throw error;
      return { data, targetModuleId };
    },
    onSuccess: (result) => {
      // Invalidate both source and target module lessons
      queryClient.invalidateQueries({ queryKey: ['admin-lessons', moduleId] });
      queryClient.invalidateQueries({ queryKey: ['admin-lessons', result.targetModuleId] });
      toast({ title: 'Lesson moved successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to move lesson', description: error.message, variant: 'destructive' });
    },
  });

  return {
    lessons,
    isLoading,
    createLesson: createLesson.mutate,
    updateLesson: updateLesson.mutate,
    deleteLesson: deleteLesson.mutate,
    moveLesson: moveLesson.mutate,
    isMovingLesson: moveLesson.isPending,
  };
}
